import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  CreditCard, 
  Calendar, 
  User, 
  Mail, 
  Phone,
  ExternalLink,
  DollarSign,
  FileText,
  Clock,
  Building,
  Wifi,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { formatCurrency, getStatusStyle } from '@/lib/payments-service';

interface PaymentDetailDialogProps {
  paymentId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface PaymentDetail {
  id: string;
  booking_id: string;
  full_name: string;
  email: string;
  phone: string;
  address: string | null;
  service: string;
  situation: string;
  appointment_start: string | null;
  appointment_end: string | null;
  appointment_type: string | null;
  payment_method: string | null;
  payment_status: string | null;
  payment_amount: number | null;
  payment_currency: string | null;
  payment_source: 'online' | 'offline' | null;
  payment_notes: string | null;
  payment_date: string | null;
  stripe_session_id: string | null;
  payment_evidence_url: string | null;
  zelle_confirmation: string | null;
  created_at: string;
  updated_at: string;
}

export default function PaymentDetailDialog({
  paymentId,
  open,
  onOpenChange,
}: PaymentDetailDialogProps) {
  const [payment, setPayment] = useState<PaymentDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open && paymentId) {
      fetchPaymentDetail();
    }
  }, [open, paymentId]);

  const fetchPaymentDetail = async () => {
    if (!paymentId) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('bookings')
        .select('*')
        .eq('id', paymentId)
        .single();

      if (error) throw error;
      setPayment(data as PaymentDetail);
    } catch (error) {
      console.error('Error fetching payment detail:', error);
    } finally {
      setLoading(false);
    }
  };

  const statusStyle = getStatusStyle(payment?.payment_status);

  const getStatusBadgeVariant = (variant: string) => {
    switch (variant) {
      case 'success':
        return 'default';
      case 'warning':
        return 'secondary';
      case 'destructive':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Payment Details
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="space-y-4">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : payment ? (
          <div className="space-y-6">
            {/* Payment Summary */}
            <div className="rounded-lg border bg-muted/30 p-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-sm text-muted-foreground">Amount</p>
                  <p className="text-3xl font-bold">
                    {formatCurrency(payment.payment_amount, payment.payment_currency || 'USD')}
                  </p>
                </div>
                <Badge 
                  variant={getStatusBadgeVariant(statusStyle.variant)}
                  className="text-sm px-3 py-1"
                >
                  {statusStyle.label}
                </Badge>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Payment Method</p>
                  <p className="font-medium capitalize">{payment.payment_method || 'Not specified'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Source</p>
                  <div className="flex items-center gap-1.5 font-medium">
                    {payment.payment_source === 'offline' ? (
                      <>
                        <Building className="h-4 w-4 text-muted-foreground" />
                        <span>Offline / In-Office</span>
                      </>
                    ) : payment.stripe_session_id ? (
                      <>
                        <Wifi className="h-4 w-4 text-muted-foreground" />
                        <span>Online (Stripe)</span>
                      </>
                    ) : (
                      <span className="text-muted-foreground">Not recorded</span>
                    )}
                  </div>
                </div>
                {payment.payment_date && (
                  <div>
                    <p className="text-muted-foreground">Payment Date</p>
                    <p className="font-medium">{format(new Date(payment.payment_date), 'PPP')}</p>
                  </div>
                )}
                <div>
                  <p className="text-muted-foreground">Currency</p>
                  <p className="font-medium">{payment.payment_currency || 'USD'}</p>
                </div>
              </div>
              {payment.payment_notes && (
                <div className="mt-4 p-3 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">Payment Notes</p>
                  <p className="text-sm">{payment.payment_notes}</p>
                </div>
              )}
            </div>

            <Separator />

            {/* Customer Information */}
            <div>
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <User className="h-4 w-4" />
                Customer Information
              </h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-muted-foreground">Name</p>
                    <p className="font-medium">{payment.full_name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-muted-foreground">Email</p>
                    <p className="font-medium">{payment.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-muted-foreground">Phone</p>
                    <p className="font-medium">{payment.phone}</p>
                  </div>
                </div>
                {payment.address && (
                  <div className="flex items-center gap-2">
                    <Building className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-muted-foreground">Address</p>
                      <p className="font-medium">{payment.address}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <Separator />

            {/* Appointment Information */}
            <div>
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Appointment Information
              </h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Booking ID</p>
                  <p className="font-medium font-mono text-xs">{payment.booking_id}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Service</p>
                  <p className="font-medium">{payment.service}</p>
                </div>
                {payment.appointment_start && (
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-muted-foreground">Appointment Date</p>
                      <p className="font-medium">
                        {format(new Date(payment.appointment_start), 'PPP p')}
                      </p>
                    </div>
                  </div>
                )}
                <div>
                  <p className="text-muted-foreground">Type</p>
                  <p className="font-medium">{payment.appointment_type || 'Not specified'}</p>
                </div>
              </div>
              <div className="mt-4">
                <Link to={`/admin/appointments`}>
                  <Button variant="outline" size="sm" className="gap-2">
                    <ExternalLink className="h-4 w-4" />
                    View Appointment
                  </Button>
                </Link>
              </div>
            </div>

            <Separator />

            {/* Payment Details - Only show for online payments */}
            {(payment.stripe_session_id || payment.zelle_confirmation || payment.payment_evidence_url) && (
              <>
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <DollarSign className="h-4 w-4" />
                    Transaction Details
                  </h3>
                  <div className="space-y-3 text-sm">
                    {payment.stripe_session_id && (
                      <div>
                        <p className="text-muted-foreground">Stripe Session ID</p>
                        <p className="font-mono text-xs bg-muted px-2 py-1 rounded break-all">
                          {payment.stripe_session_id}
                        </p>
                      </div>
                    )}
                    {payment.zelle_confirmation && (
                      <div>
                        <p className="text-muted-foreground">Zelle Confirmation</p>
                        <p className="font-medium">{payment.zelle_confirmation}</p>
                      </div>
                    )}
                    {payment.payment_evidence_url && (
                      <div>
                        <p className="text-muted-foreground">Payment Evidence</p>
                        <a 
                          href={payment.payment_evidence_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-primary hover:underline flex items-center gap-1"
                        >
                          View Evidence <ExternalLink className="h-3 w-3" />
                        </a>
                      </div>
                    )}
                  </div>
                </div>
                <Separator />
              </>
            )}

            {/* Timestamps */}
            <div>
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Record Information
              </h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Created At</p>
                  <p className="font-medium">
                    {format(new Date(payment.created_at), 'PPP p')}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Last Updated</p>
                  <p className="font-medium">
                    {format(new Date(payment.updated_at), 'PPP p')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            Payment not found
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
